/**
 * formatting.ts - Utility functions for formatting data
 */

import type { Player } from '../../types/api';
import { t } from './i18n-helper';

/**
 * Escape HTML special characters to prevent XSS
 */
function escapeHtml(text: string | null | undefined): string {
  if (!text) return '';
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Options for player display formatting
 */
export interface PlayerDisplayOptions {
  showRating?: boolean;
  showFederation?: boolean;
  showTitle?: boolean;
  showScore?: boolean;
  score?: number | null;
}

/**
 * Format player display with name, title, rating, federation, and optional score
 * @param player - Player object
 * @param options - Display options
 * @returns HTML string for player display
 */
export function formatPlayerDisplay(
  player: Player | null | undefined,
  options: PlayerDisplayOptions = {}
): string {
  if (!player) return 'Unknown Player';

  const {
    showRating = true,
    showFederation = true,
    showTitle = true,
    showScore = false,
    score = null
  } = options;

  const title = showTitle && player.title ? `${escapeHtml(player.title)} ` : '';
  const firstName = escapeHtml(player.firstName || '');
  const lastName = escapeHtml(player.lastName || '');
  const playerName = `${firstName} ${lastName}`.trim() || 'Unknown Player';
  const rating = showRating && player.rating
    ? ` <span style="color: #666; font-size: 0.9em;">(${player.rating})</span>`
    : '';
  const federation = showFederation && player.federation
    ? ` <span style="color: #666; font-size: 0.9em;">${escapeHtml(player.federation)}</span>`
    : '';

  let scoreStr = '';
  if (showScore && score !== null && score !== undefined) {
    const normalized = score % 1 === 0 ? score.toString() : score.toFixed(1);
    scoreStr = ` <span style="color: #666; font-size: 0.9em;">[${normalized}]</span>`;
  }

  return `${title}${playerName}${rating}${federation}${scoreStr}`;
}

/**
 * Get current locale from i18n system
 * Maps user's selected language to appropriate locale for date formatting
 */
function getLocale(): string {
  if (window.i18n?.getLocale) {
    return window.i18n.getLocale();
  }

  // Fallback: check localStorage for user's selected language
  const savedLang = localStorage.getItem('language');
  if (savedLang) {
    const localeMap: Record<string, string> = {
      'ar': 'ar-SA', 'bg': 'bg-BG', 'bn': 'bn-BD', 'cs': 'cs-CZ',
      'da': 'da-DK', 'de': 'de-DE', 'el': 'el-GR', 'en': 'en-US',
      'es': 'es-ES', 'fi': 'fi-FI', 'fr': 'fr-FR', 'hi': 'hi-IN',
      'hu': 'hu-HU', 'id': 'id-ID', 'it': 'it-IT', 'ja': 'ja-JP',
      'ko': 'ko-KR', 'no': 'no-NO', 'pl': 'pl-PL', 'pt': 'pt-PT',
      'ro': 'ro-RO', 'ru': 'ru-RU', 'sr': 'sr-RS', 'sv': 'sv-SE',
      'tr': 'tr-TR', 'uk': 'uk-UA', 'zh': 'zh-CN'
    };
    return localeMap[savedLang] || savedLang;
  }

  // Last resort: use browser locale
  return navigator.language || 'en-US';
}

/**
 * Format tournament date range nicely
 * @param startDate - Start date ISO string
 * @param endDate - End date ISO string
 * @returns Formatted date range
 */
export function formatTournamentDate(startDate: string, endDate?: string): string {
  const locale = getLocale();
  const start = new Date(startDate);
  const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };

  if (!endDate) {
    return start.toLocaleDateString(locale, options);
  }

  const end = new Date(endDate);

  // Same year
  if (start.getFullYear() === end.getFullYear()) {
    // Same month
    if (start.getMonth() === end.getMonth()) {
      // Same day
      if (start.getDate() === end.getDate()) {
        return start.toLocaleDateString(locale, options);
      }
      // Different days, same month
      return `${start.toLocaleDateString(locale, { month: 'short', day: 'numeric' })} - ${end.getDate()}, ${end.getFullYear()}`;
    }
    // Different months, same year
    return `${start.toLocaleDateString(locale, { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString(locale, { month: 'short', day: 'numeric' })}, ${end.getFullYear()}`;
  }

  // Different years
  return `${start.toLocaleDateString(locale, options)} - ${end.toLocaleDateString(locale, options)}`;
}

/**
 * Format date with weekday for round display (e.g., "Saturday, 1.11.2025")
 * @param dateStr - Date ISO string (can be date-only or full timestamp)
 * @param time - Optional time string
 * @returns Formatted date with weekday
 */
export function formatRoundDateTime(dateStr: string, time?: string): string {
  const locale = getLocale();
  // Extract just the date part (YYYY-MM-DD) to handle both date-only strings and full ISO timestamps
  const datePart = dateStr.split('T')[0];
  const date = new Date(datePart + 'T00:00:00');
  const formatted = date.toLocaleDateString(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'numeric',
    year: 'numeric'
  });

  if (!time) return formatted;

  const atWord = t('pairings.at', 'at');
  return atWord ? `${formatted} ${atWord} ${time}` : `${formatted} ${time}`;
}

/**
 * Get knockout round name (e.g., Final, Semi-final, Quarter-final)
 * @param round - Current round number
 * @param totalRounds - Total number of rounds
 * @returns Round name
 */
export function getKnockoutRoundName(round: number, totalRounds: number): string {
  const roundsFromEnd = totalRounds - round;
  if (roundsFromEnd === 0) return t('knockout.final', 'Final');
  if (roundsFromEnd === 1) return t('knockout.semiFinal', 'Semi-final');
  if (roundsFromEnd === 2) return t('knockout.quarterFinal', 'Quarter-final');
  return `${t('knockout.round', 'Round')} ${round}`;
}

/**
 * Simplify venue name by removing multi-language country variants
 * e.g., "Street, City, Schweiz/Suisse/Svizzera/Svizra" -> "Street, City, Schweiz"
 * @param venue - Venue string
 * @returns Simplified venue string
 */
export function simplifyVenueName(venue: string | null | undefined): string {
  if (!venue) return '';

  // Split by comma and simplify each part (remove multi-language variants after /)
  return venue.split(',')
    .map(part => {
      const trimmed = part.trim();
      // If part contains /, take only the first variant
      return trimmed.includes('/') ? trimmed.split('/')[0].trim() : trimmed;
    })
    .join(', ');
}
